import React, { useEffect, useRef, useState } from "react";
import { aiApi } from "@/lib/api";
import { PageShell } from "./Overview";
import { Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CONTEXTS = [
  { id: "jurisdiction", label: "Jurisdiction advisor", hint: "Which country & entity type is right for me?" },
  { id: "compliance", label: "Compliance Q&A", hint: "Explain this filing or deadline to me." },
  { id: "forms", label: "Form-fill assistant", hint: "Walk me through an incorporation form." },
  { id: "general", label: "General", hint: "Ask anything about Artha." },
];

const SUGGESTIONS = {
  jurisdiction: [
    "I'm a 2-person AI SaaS aiming to raise from US VCs. Where do I incorporate?",
    "I ship software from India but sell to EU customers. Estonia or UK?",
    "Best jurisdiction for a crypto infra team — Switzerland vs Singapore vs Dubai?",
  ],
  compliance: [
    "Explain the BOI Report for a Delaware LLC",
    "What is Director DIR-3 KYC in India and when is it due?",
    "When do I need to register for UK VAT?",
  ],
  forms: ["What do I need to fill Form SPICe+ Part A for India?", "What documents do I need for an Estonian OÜ?"],
  general: ["What does Artha do differently from Stripe Atlas?"],
};

const Assistant = () => {
  const [context, setContext] = useState("jurisdiction");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: msg }]);
    setSending(true);
    try {
      const res = await aiApi.chat({ session_id: sessionId, message: msg, context });
      setSessionId(res.session_id);
      setMessages((m) => [...m, { id: res.assistant_message.id, role: "assistant", content: res.assistant_message.content }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI error");
    } finally {
      setSending(false);
    }
  };

  const switchContext = (id) => {
    setContext(id);
    setMessages([]);
    setSessionId(null);
  };

  return (
    <PageShell title="AI Assistant"
      subtitle="Claude Sonnet 4.5 · Artha-grounded"
      data-testid="ai-assistant-page"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-230px)]">
        <aside className="lg:col-span-3 border border-border-subtle bg-surface-card p-4 overflow-auto">
          <div className="text-xs font-mono-accent uppercase tracking-[0.2em] text-ink-muted mb-3">Mode</div>
          {CONTEXTS.map((c) => (
            <button key={c.id} onClick={() => switchContext(c.id)}
              data-testid={`context-${c.id}`}
              className={`w-full text-left px-3 py-3 text-sm mb-1 transition-colors ${
                context === c.id ? "bg-surface-app text-artha-amber" : "text-ink-body hover:bg-surface-app"
              }`}>
              <div>{c.label}</div>
              <div className="text-xs text-ink-muted mt-1">{c.hint}</div>
            </button>
          ))}
        </aside>
        <section className="lg:col-span-9 border border-border-subtle bg-surface-card flex flex-col min-h-0">
          <div className="flex-1 overflow-auto p-6 space-y-5" data-testid="chat-transcript">
            {messages.length === 0 && (
              <div className="h-full flex flex-col justify-center items-start">
                <Sparkles size={28} className="text-artha-amber mb-4" />
                <div className="h-display text-3xl">Ask me anything.</div>
                <p className="text-sm text-ink-muted mt-2 max-w-md">
                  I'm trained on global incorporation & compliance across seven jurisdictions.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {(SUGGESTIONS[context] || []).map((s, i) => (
                    <button key={i} onClick={() => send(s)} data-testid={`suggestion-${i}`}
                      className="text-left text-xs font-mono-accent uppercase tracking-[0.18em] px-3 py-2 border border-border-subtle hover:border-artha-amber hover:text-artha-amber transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
                <div className={`max-w-[85%] p-4 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-artha-amber text-artha-ink"
                    : "border border-border-subtle text-ink-body"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="border border-border-subtle p-4 text-sm text-ink-muted">
                  <span className="inline-block w-2 h-2 bg-artha-amber rounded-full animate-pulse mr-1" />
                  <span className="inline-block w-2 h-2 bg-artha-amber rounded-full animate-pulse mr-1" style={{ animationDelay: "120ms" }} />
                  <span className="inline-block w-2 h-2 bg-artha-amber rounded-full animate-pulse" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border-subtle p-4 flex gap-3">
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Artha…"
              data-testid="chat-input"
              className="flex-1 bg-transparent border border-border-subtle px-4 py-3 text-sm focus:outline-none focus:border-artha-amber" />
            <button type="submit" disabled={sending || !input.trim()} data-testid="chat-send-button"
              className="inline-flex items-center gap-2 bg-artha-amber text-artha-ink px-5 text-sm hover:bg-artha-cream transition-colors disabled:opacity-50">
              <Send size={14} /> Send
            </button>
          </form>
        </section>
      </div>
    </PageShell>
  );
};

export default Assistant;
